import { useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { CustomerPicker, LoadingOverlay, RECEIVABLES_API, money, todayIso, useReceivableLookups, useReceivableSettings } from "./common";

const emptyForm = {
  salesmanUserId: "",
  beatId: "",
  customer: null,
  invoiceNumber: "",
  invoiceDate: todayIso(),
  dueDate: "",
  invoiceAmount: "",
  balanceAmount: "",
  loadSheetNumber: "",
};

/** Key in an outstanding bill by hand (legacy addInvoice.aspx) — header only, no line items. */
const AddInvoice = () => {
  const { salesmen, beats } = useReceivableLookups(["salesmen", "beats"]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  // Beat-wise, the beat picked here is the one the bill is assigned to; customer-wise it only narrows the customer list.
  const settings = useReceivableSettings();
  const beatWise = settings?.invoiceCustomerWise === false;

  const beatOptions = form.salesmanUserId
    ? beats.filter((beat) => String(beat.salesmanUserId) === String(form.salesmanUserId))
    : beats;

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer) {
      toast.error("Select a customer from the list");
      return;
    }
    const invoiceAmount = Number(form.invoiceAmount);
    const balanceAmount = form.balanceAmount === "" ? invoiceAmount : Number(form.balanceAmount);
    if (!(invoiceAmount > 0)) {
      toast.error("Invoice amount must be greater than zero");
      return;
    }
    if (balanceAmount < 0 || balanceAmount > invoiceAmount) {
      toast.error("Balance must be between 0 and the invoice amount");
      return;
    }
    setSaving(true);
    try {
      const result = await api.post(`${RECEIVABLES_API}/manual-invoices`, {
        customerId: form.customer.id,
        invoiceNumber: form.invoiceNumber.trim(),
        invoiceDate: form.invoiceDate,
        dueDate: form.dueDate || null,
        invoiceAmount,
        balanceAmount,
        salesmanUserId: form.salesmanUserId ? Number(form.salesmanUserId) : null,
        beatId: form.beatId ? Number(form.beatId) : null,
        loadSheetNumber: form.loadSheetNumber.trim() || null,
      });
      toast.success(`Invoice ${result.invoiceNumber} created successfully`);
      setLastResult(result);
      setForm({ ...emptyForm, invoiceDate: todayIso() });
    } catch (error) {
      toast.error(error.message || "Invoice creation failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={saving} label="Saving invoice..." />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Enter an outstanding bill raised outside the app (for example an opening balance). Only the header is recorded — no
        items or stock movement. The balance is what the customer still owes on it.
      </div>

      <div className="page-panel" style={{ maxWidth: 820 }}>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Salesman</label>
              <select
                value={form.salesmanUserId}
                onChange={(e) => setForm((prev) => ({ ...prev, salesmanUserId: e.target.value, beatId: "" }))}
              >
                <option value="">--- All ---</option>
                {salesmen.map((s) => (
                  <option key={s.userId} value={s.userId}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{beatWise ? "Assign to beat" : "Beat"}</label>
              <select value={form.beatId} onChange={(e) => set("beatId", e.target.value)}>
                <option value="">{beatWise ? "--- Customer's beat (default) ---" : "--- All beats ---"}</option>
                {beatOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Customer *</label>
              <CustomerPicker
                value={form.customer}
                onChange={(customer) => set("customer", customer)}
                beatId={beatWise ? undefined : form.beatId || undefined}
                width="100%"
              />
            </div>
            <div className="form-group">
              <label>Invoice No *</label>
              <input value={form.invoiceNumber} onChange={(e) => set("invoiceNumber", e.target.value)} maxLength={30} required />
            </div>
            <div className="form-group">
              <label>Load Sheet No</label>
              <input value={form.loadSheetNumber} onChange={(e) => set("loadSheetNumber", e.target.value)} maxLength={50} />
            </div>
            <div className="form-group">
              <label>Invoice Date *</label>
              <input type="date" value={form.invoiceDate} onChange={(e) => set("invoiceDate", e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Due Date</label>
              <input type="date" value={form.dueDate} min={form.invoiceDate} onChange={(e) => set("dueDate", e.target.value)} />
            </div>
            <div className="form-group">
              <label>Invoice Amount *</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={form.invoiceAmount}
                onChange={(e) => set("invoiceAmount", e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Balance Amount (defaults to invoice amount)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.balanceAmount}
                placeholder={form.invoiceAmount}
                onChange={(e) => set("balanceAmount", e.target.value)}
              />
            </div>
          </div>
          <div className="modal-actions">
            <button type="button" className="secondary" onClick={() => setForm({ ...emptyForm, invoiceDate: todayIso() })}>
              Clear
            </button>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? "Saving..." : "Save Invoice"}
            </button>
          </div>
        </form>
      </div>

      {lastResult ? (
        <div className="page-panel" style={{ maxWidth: 820, marginTop: 16 }}>
          <strong>Last saved: {lastResult.invoiceNumber}</strong>
          <div className="page-subtitle" style={{ marginTop: 6 }}>
            Total outstanding moved from ₹{money(lastResult.previousOutstanding)} to ₹{money(lastResult.newOutstanding)}.
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AddInvoice;
